import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  KeyIcon,
  PaperAirplaneIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { PMField } from "../../../utils/PMField";
import { PMButton } from "../../../utils/PMButton";
import RoleOnly from "../../../../routes/roleOnly";
import {
  sendPasswordSetup,
  setUserPassword,
} from "../../../../api/user";
import { intlLocaleFor } from "../../../../i18n/languages";

const SOURCE_I18N = {
  create: "company.users.password.source_create",
  resend: "company.users.password.source_resend",
  admin_send: "company.users.password.source_admin_send",
  tripletex_sync: "company.users.password.source_tripletex_sync",
  admin_password_set: "company.users.password.source_admin_password_set",
};

function formatSentAt(value, language) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(intlLocaleFor(language));
  } catch {
    return value;
  }
}

export function UserPasswordAccess({ item, onReload }) {
  const { t, i18n } = useTranslation();
  const [sending, setSending] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const canSend = !!item?.can_send_password_setup;
  const deliveries = Array.isArray(item?.invitation_deliveries)
    ? item.invitation_deliveries
    : [];

  const handleSendLink = async () => {
    if (!item?.id || sending) return;
    setSending(true);
    try {
      // SessionExpiredModal interceptor may resolve 4xx/5xx as error.response —
      // treat payload.errors as failure (same pattern as users-table.js).
      const response = await sendPasswordSetup(item.id);
      if (response?.errors?.length) {
        toast.error(response.errors.join(", "));
        return;
      }
      toast.success(
        response?.message || t("company.users.password.send_success")
      );
      await onReload?.();
    } catch (err) {
      const msg =
        err?.errors?.join?.(", ") ||
        err?.errors?.[0] ||
        err?.message ||
        t("company.users.password.send_error");
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleSetPassword = async () => {
    if (!item?.id || savingPassword) return;
    if (!password || password.length < 8) {
      toast.error(t("company.users.password.too_short"));
      return;
    }
    if (password !== passwordConfirmation) {
      toast.error(t("company.users.password.mismatch"));
      return;
    }
    setSavingPassword(true);
    try {
      const response = await setUserPassword(
        item.id,
        password,
        passwordConfirmation
      );
      if (response?.errors?.length) {
        toast.error(response.errors.join(", "));
        return;
      }
      toast.success(
        response?.message || t("company.users.password.set_success")
      );
      setPasswordOpen(false);
      setPassword("");
      setPasswordConfirmation("");
      await onReload?.();
    } catch (err) {
      const msg =
        err?.errors?.join?.(", ") ||
        err?.errors?.[0] ||
        err?.message ||
        t("company.users.password.set_error");
      toast.error(msg);
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <RoleOnly role="admin">
      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
        <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EAF3FD] text-[#1463C7]">
              <KeyIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-slate-900">
                {t("company.users.password.section_title")}
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                {t("company.users.password.section_subtitle")}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-5 py-4 sm:px-6">
          <div className="flex flex-wrap gap-2">
            {canSend ? (
              <PMButton
                type="button"
                styleType="primary"
                disabled={sending}
                onClick={handleSendLink}
              >
                <span className="inline-flex items-center">
                  <PaperAirplaneIcon className="mr-1.5 h-4 w-4" />
                  {sending
                    ? t("company.users.password.sending")
                    : t("company.users.password.send_link")}
                </span>
              </PMButton>
            ) : null}
            <PMButton
              type="button"
              styleType="secondary"
              onClick={() => setPasswordOpen(true)}
            >
              <span className="inline-flex items-center">
                <KeyIcon className="mr-1.5 h-4 w-4" />
                {t("company.users.password.set_password")}
              </span>
            </PMButton>
          </div>

          {item?.last_invitation_sent_at ? (
            <p className="text-sm text-slate-600">
              {t("company.users.password.last_sent", {
                at: formatSentAt(item.last_invitation_sent_at, i18n.language),
              })}
            </p>
          ) : (
            <p className="text-sm text-slate-500">
              {t("company.users.password.never_sent")}
            </p>
          )}

          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <ClockIcon className="h-4 w-4 text-slate-400" />
              {t("company.users.password.history_title")}
            </div>
            {deliveries.length === 0 ? (
              <p className="text-sm text-slate-500">
                {t("company.users.password.history_empty")}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
                {deliveries.map((row) => (
                  <li
                    key={row.id}
                    className="flex flex-col gap-1 px-3 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800">
                        {t(
                          SOURCE_I18N[row.source] ||
                            "company.users.password.source_unknown"
                        )}
                      </div>
                      <div className="truncate text-slate-500">
                        {row.email}
                        {row.sent_by?.name || row.sent_by?.displayTitle
                          ? ` · ${row.sent_by?.displayTitle || row.sent_by?.name}`
                          : ""}
                      </div>
                    </div>
                    <div className="shrink-0 tabular-nums text-slate-500">
                      {formatSentAt(row.sent_at, i18n.language)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <PMDialog
        open={passwordOpen}
        onClose={() => !savingPassword && setPasswordOpen(false)}
        eyebrow="company.users.password.section_title"
        title="company.users.password.set_password"
        subtitle="company.users.password.set_subtitle"
        size="lg"
      >
        <div className="space-y-4">
          <PMField
            formik={false}
            label={t("company.users.password.new_password")}
            name="admin_set_password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
          <PMField
            formik={false}
            label={t("company.users.password.confirm_password")}
            name="admin_set_password_confirmation"
            type="password"
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            autoComplete="new-password"
          />
          <div className="pm-form-footer flex justify-end gap-2">
            <PMButton
              type="button"
              styleType="secondary"
              disabled={savingPassword}
              onClick={() => setPasswordOpen(false)}
            >
              {t("company.users.password.cancel")}
            </PMButton>
            <PMButton
              type="button"
              styleType="primary"
              disabled={savingPassword}
              onClick={handleSetPassword}
            >
              {savingPassword
                ? t("company.users.password.saving")
                : t("company.users.password.save")}
            </PMButton>
          </div>
        </div>
      </PMDialog>
    </RoleOnly>
  );
}
